import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * A callback that waits for the typing to stop.
 *
 * Every list page searches server-side, so the question is not whether the
 * search runs as you type -- it should, nobody wants to reach for an Apply
 * button to see a list narrow -- but how many requests that costs. Wired
 * straight to onChange, "trainer" is eight requests and the last one to
 * come back wins, which is not necessarily the last one sent.
 *
 * So the box updates on every keystroke and this runs once the keystrokes
 * stop. Pending calls are dropped: only the newest arguments ever run.
 *
 * `fn` is held in a ref, so the timer always calls the current closure --
 * the returned function is stable and safe to name in a dependency array,
 * and the caller does not have to memoise what it passes in.
 *
 * The returned function carries two escapes, the same pair lodash's
 * debounce has:
 *   .flush(...args)  run now, cancelling anything pending -- for Enter, or
 *                    for an Apply button that must not be beaten by a
 *                    keystroke still in the air
 *   .cancel()        drop what is pending -- for Clear, which sets the
 *                    search itself and does not want the old one landing
 *                    on top of it a moment later
 *
 * Written out rather than taken from lodash/debounce: lodash is in
 * node_modules as somebody else's transitive dependency and is not in this
 * project's package.json, and a hook every list page leans on should not
 * rest on that.
 *
 * @param {Function} fn     what to run once the value settles
 * @param {number}   delay  ms of quiet before it runs
 * @returns {Function} debounced fn, plus .flush(...args) and .cancel()
 */
export default function useDebouncedCallback(fn, delay = 400) {
  const latest = useRef(fn);
  latest.current = fn;

  const timer = useRef(null);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /* A call still in the air after the page has gone would set state on an
     unmounted component. Leaving the route mid-word is enough to do it. */
  useEffect(() => cancel, [cancel]);

  return useMemo(() => {
    const run = (...args) => {
      cancel();
      timer.current = setTimeout(() => {
        timer.current = null;
        latest.current(...args);
      }, delay);
    };

    run.flush = (...args) => {
      cancel();
      latest.current(...args);
    };

    run.cancel = cancel;

    return run;
  }, [cancel, delay]);
}
